import { useState, useEffect, useCallback } from 'react'
import { Trash2, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { tokens } from '@/locales/translations/tokens'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { getUserMemories, deleteUserMemory } from '@/api/memory'
import { toast } from '@/hooks/use-toast'
import { debounce } from 'lodash'
import { Input } from '@/components/ui/input'
import GeneralPagination from '@/components/general-pagination'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface Memory {
  id: string
  fact: string
  context: string
}

export interface MemoryPaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export function MemoryDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [memories, setMemories] = useState<Memory[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false)
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchFilter, setSearchFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const ITEMS_PER_PAGE = 6

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setSearchFilter(value)
    }, 500),
    []
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    debouncedSetSearch(e.target.value)
  }

  const loadMemories = async () => {
    setIsLoading(true)
    try {
      const response = await getUserMemories({
        page,
        items_per_page: ITEMS_PER_PAGE,
        search: searchFilter,
      })
      
      const memoriesWithIds = response.memories.map((memory) => ({
        id: memory.external_id,
        fact: memory.fact,
        context: memory.context,
      }))
      
      setMemories(memoriesWithIds)
      setTotalPages(Math.ceil(response.total_items / ITEMS_PER_PAGE))
    } catch (_error) {
      setMemories([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteMemoryConfirm = async () => {
    if (itemToDelete) {
      try {
        await deleteUserMemory({
          memory_ids: [itemToDelete],
        })
        setMemories((prev) => prev.filter((memory) => memory.id !== itemToDelete))
      } catch (_error) {
        toast({
          title: t(tokens.ui.toasts.error.title),
          description: t(tokens.ui.toasts.error.generic),
        })
      } finally {
        setItemToDelete(null)
        setIsDeleteItemDialogOpen(false)
      }
    }
  }

  const handleDeleteMemoryRequest = (id: string) => {
    setItemToDelete(id)
    setIsDeleteItemDialogOpen(true)
  }

  const handleDeleteAllMemories = async () => {
    try {
      const memoryIds = memories.map(memory => memory.id)
      await deleteUserMemory({
        memory_ids: memoryIds,
      })
      setMemories([])
    } catch (_error) {
      toast({
        title: t(tokens.ui.toasts.error.title),
        description: t(tokens.ui.toasts.error.generic),
      })
    } finally {
      setIsDeleteAllDialogOpen(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleDialogChange = (isOpen: boolean) => {
    if (!isOpen) {
      setPage(1)
      setSearchInput('')
      setSearchFilter('')
    }
    onOpenChange(isOpen)
  }

  useEffect(() => {
    if (open) {
      loadMemories()
    }
  }, [open, page, searchFilter])

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-[800px] flex flex-col overflow-hidden p-0">
          <div className="p-6 pb-2">
            <DialogHeader>
              <DialogTitle>{t(tokens.workstation.memory.title)}</DialogTitle>
              <DialogDescription>
                {t(tokens.workstation.memory.subtitle)}
              </DialogDescription>
            </DialogHeader>
          </div>
          
          <div className="px-6 mb-2 pb-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar memórias..."
                value={searchInput}
                onChange={handleSearchChange}
                className="pl-8"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto px-6 pb-2" style={{ height: "50vh" }}>
            {isLoading ? (
              <div className="h-full flex items-center justify-center" style={{ minHeight: '50vh' }}>
                <p>{t(tokens.ui.common.loading) || 'Carregando...'}</p>
              </div>
            ) : memories.length === 0 ? (
              <div className="h-full flex items-center justify-center" style={{ minHeight: '50vh' }}>
                <p>Nenhuma memória encontrada</p>
              </div>
            ) : (
              <div className="space-y-3 h-full py-2" style={{ minHeight: '50vh' }}>
                {memories.map((memory) => (
                  <div
                    key={memory.id}
                    className="p-4 rounded-md bg-background border-border border flex justify-between items-center"
                  >
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-medium truncate">{memory.fact}</p>
                        </TooltipTrigger>
                        <TooltipContent className="bg-background text-foreground">
                          <p>{memory.fact}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 ml-2 text-muted-foreground hover:text-destructive flex-shrink-0"
                      onClick={() => handleDeleteMemoryRequest(memory.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="px-6 py-2 bg-background">
            <div className="flex justify-end mb-2">
              {memories.length > 0 && (
                <GeneralPagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
          
          <DialogFooter className="p-4 border-t border-border bg-background">
            <Button
              variant="destructive"
              onClick={() => setIsDeleteAllDialogOpen(true)}
              disabled={memories.length === 0}
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t(tokens.ui.actions.deleteAll)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={isDeleteAllDialogOpen}
        onOpenChange={setIsDeleteAllDialogOpen}
        title="Excluir todas as memórias"
        desc="Tem certeza que deseja excluir todas as memórias? Esta ação não pode ser desfeita."
        destructive
        handleConfirm={handleDeleteAllMemories}
      />

      <ConfirmDialog
        open={isDeleteItemDialogOpen}
        onOpenChange={setIsDeleteItemDialogOpen}
        title="Excluir memória"
        desc="Tem certeza que deseja excluir esta memória? Esta ação não pode ser desfeita."
        destructive
        handleConfirm={handleDeleteMemoryConfirm}
      />
    </>
  )
}